import { useState } from "react";
import {
  Stack,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  Button,
  IconButton,
  FormControl,
  FormLabel,
  Skeleton,
  useToast,
} from "@chakra-ui/react";
import { SectionCard } from "../../../shared/ui";
import { Plus, Trash2, Repeat } from "../../../shared/icons";
import { isPlanoBloqueado } from "../../../shared/api/planoBloqueado";
import { apiErrorMessage } from "../../../shared/api/apiErrorMessage";
import { useShiftPatternsQuery, useCreateShiftPatternMutation, useDeleteShiftPatternMutation } from "../hooks/shiftPatternQueries";
import { describeShiftPattern, FREQUENCY_OPTIONS, WEEK_OF_MONTH_OPTIONS, WEEKDAY_OPTIONS } from "../utils/describeShiftPattern";

function errorToast(toast, title, error) {
  if (isPlanoBloqueado(error)) return;
  toast({
    title,
    description: apiErrorMessage(error),
    status: "error",
    duration: 5000,
    isClosable: true,
  });
}

// Escala fixa do vínculo: no semanal e no quinzenal o médico informa a data do primeiro
// plantão e o dia da semana sai dela; no mensal ele escolhe a ordem e o dia da semana
// ("1º sábado") e a data diz a partir de quando vale. Os plantões em si só nascem quando
// ele gera a escala do mês (Calendário/Dashboard), depois de conferir.
export default function ShiftPatternsCard({ vinculoId, onGenerate }) {
  const toast = useToast();
  const { data: patterns = [], isLoading } = useShiftPatternsQuery(vinculoId);
  const createMutation = useCreateShiftPatternMutation();
  const deleteMutation = useDeleteShiftPatternMutation();
  const [startDate, setStartDate] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [weekOfMonth, setWeekOfMonth] = useState(String(WEEK_OF_MONTH_OPTIONS[0].value));
  const [weekday, setWeekday] = useState("6");
  const isMonthly = frequency === "monthly";

  const add = (event) => {
    event.preventDefault();
    if (!startDate) return;
    const rule = isMonthly
      ? { vinculoId, startDate, frequency, weekOfMonth: Number(weekOfMonth), weekday: Number(weekday) }
      : { vinculoId, startDate, frequency };
    createMutation.mutate(
      rule,
      {
        onSuccess: () => {
          toast({ title: "Plantão fixo adicionado à escala", status: "success", duration: 4000 });
          setStartDate("");
        },
        onError: (error) => errorToast(toast, "Não foi possível adicionar o plantão fixo", error),
      }
    );
  };

  const remove = (pattern) =>
    deleteMutation.mutate(pattern.id, {
      onSuccess: () => toast({ title: "Plantão fixo removido da escala", status: "success", duration: 4000 }),
      onError: (error) => errorToast(toast, "Não foi possível remover o plantão fixo", error),
    });

  return (
    <SectionCard
      title="Escala fixa"
      action={
        patterns.length > 0 && onGenerate ? (
          <Button size="sm" variant="outline" leftIcon={<Repeat size={14} />} onClick={onGenerate}>
            Gerar escala do mês
          </Button>
        ) : null
      }
      mb={6}
    >
      <Text fontSize="sm" color="app-muted-fg" mb={4}>
        Cadastre os plantões que se repetem. No início de cada mês é só conferir e gerar a escala — os plantões entram com o
        valor e as horas deste vínculo.
      </Text>

      {isLoading ? (
        <Skeleton height="40px" mb={4} />
      ) : patterns.length > 0 ? (
        <VStack align="stretch" spacing={2} mb={4}>
          {patterns.map((pattern) => {
            const { rule, since } = describeShiftPattern(pattern);
            return (
              <HStack key={pattern.id} justify="space-between" p={2} borderRadius="md" border="1px solid" borderColor="app-border">
                <HStack spacing={2} minW={0}>
                  <Repeat size={14} />
                  <Text fontSize="sm" fontWeight="medium">
                    {rule}
                  </Text>
                  <Text fontSize="xs" color="app-muted-fg">
                    {since}
                  </Text>
                </HStack>
                <IconButton
                  aria-label={`Remover ${rule.toLowerCase()}`}
                  icon={<Trash2 size={14} />}
                  size="sm"
                  variant="ghost"
                  isLoading={deleteMutation.isPending && deleteMutation.variables === pattern.id}
                  onClick={() => remove(pattern)}
                />
              </HStack>
            );
          })}
        </VStack>
      ) : null}

      <Stack as="form" onSubmit={add} direction={{ base: "column", md: "row" }} spacing={3} align={{ base: "stretch", md: "flex-end" }}>
        <FormControl>
          <FormLabel fontSize="sm">{isMonthly ? "A partir de" : "Data do primeiro plantão"}</FormLabel>
          <Input type="date" size="sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Frequência</FormLabel>
          <Select size="sm" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>
        {isMonthly ? (
          <>
            <FormControl>
              <FormLabel fontSize="sm">Ordem no mês</FormLabel>
              <Select size="sm" value={weekOfMonth} onChange={(e) => setWeekOfMonth(e.target.value)}>
                {WEEK_OF_MONTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Dia da semana</FormLabel>
              <Select size="sm" value={weekday} onChange={(e) => setWeekday(e.target.value)}>
                {WEEKDAY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </>
        ) : null}
        <Button
          type="submit"
          size="sm"
          leftIcon={<Plus size={14} />}
          flexShrink={0}
          isDisabled={!startDate}
          isLoading={createMutation.isPending}
        >
          Adicionar
        </Button>
      </Stack>
    </SectionCard>
  );
}
